import Label from '@/gf/components/Label'
import ModalForm from '@/gf/components/ModalForm'
import TextField from '@/gf/components/TextField'
import DateInput from '@/gf/components/inputs/Date'
import PriceInput from '@/gf/components/inputs/Price'
import useAddAltMpn from '@/gf/hooks/useAddAltMpn'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import MoneyM from '@/gf/modules/Money'
import TimeM from '@/gf/modules/Time'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import * as api from '@/suppliers/modules/api'
import { GraphQLError } from 'graphql'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import {
  Address,
  EditLineItem,
  EditLineItemChanges,
  Maybe,
  Money,
  ProductSearchResult,
  ShippingCost,
} from '../../types'
import CreateQuoteStoreFormM from '../modules/CreateQuoteStoreForm'
import StoreOrderM from '../modules/StoreOrder'
import { CreateQuoteForm, CreateQuoteStoreForm, NewStoreOrder } from '../types'
import NewStoreOrderCard from './NewStoreOrderCard'

const NewStoreOrders = ({
  newStoreOrders,
  shippingCosts,
  setShippingCosts,
  setAdditions,
  additions,
  productsFromQuery,
  setProductsFromQuery,
  form,
  setForm,
}: {
  newStoreOrders: NewStoreOrder[]
  shippingCosts: ShippingCost[]
  setShippingCosts: React.Dispatch<React.SetStateAction<ShippingCost[]>>
  setAdditions: React.Dispatch<React.SetStateAction<EditLineItemChanges>>
  additions: EditLineItemChanges
  productsFromQuery: ProductSearchResult[]
  setProductsFromQuery: React.Dispatch<React.SetStateAction<ProductSearchResult[]>>
  form: CreateQuoteForm
  setForm: React.Dispatch<React.SetStateAction<CreateQuoteForm>>
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [shippingStoreId, setShippingStoreId] = useState()
  const [shippingModalOpen, shippingModalToggler] = useToggle()
  const [altMpnModalOpen, altMpnModalToggler] = useToggle()
  const [availabilityModalOpen, availabilityModalToggler] = useToggle()
  const addAltMpn = useAddAltMpn()
  const [altMpn, setAltMpn] = useState<string>('')
  const [shippingText, setShippingText] = useState('0')
  const [suggestedShippingCostByStoreId, setSuggestedShippingCostByStoreId] =
    useState<Record<string, Maybe<Money>>>()

  useEffect(() => {
    async function getShippingCosts() {
      const lineItemInputsByStoreId = newStoreOrders.map((nso) => ({
        storeId: nso.store.id,
        lineItemInputs: nso.lineItems.map((li) => ({
          productId: li.product.id,
          inStock: li.inStock,
          availableAt: li.availableAt,
          quantity: li.quantity,
          unitPrice: MoneyM.toDecimal(li.unitPrice),
        })),
      }))
      const calculatedShippingCosts = await Promise.all(
        lineItemInputsByStoreId.map(async ({ storeId, lineItemInputs }) => {
          const shippingCost = await api.calculateShippingCost(storeId, lineItemInputs)
          return {
            storeId,
            shippingCost,
          }
        })
      )

      const calculatedShippingCostsByStoreId = calculatedShippingCosts.reduce(
        (acc, { storeId, shippingCost }) => ({
          ...acc,
          [storeId]: shippingCost,
        }),
        {}
      )
      setSuggestedShippingCostByStoreId(calculatedShippingCostsByStoreId)
    }
    getShippingCosts()
  }, [newStoreOrders])

  const [activeLineItem, setActiveLineItem] = useState<EditLineItem>()
  const [quantityText, setQuantityText] = useState('')
  const [availableAt, setAvailableAt] = useState<DateTime | undefined>()
  const [unitPriceText, setUnitPriceText] = useState('')

  const [adjustQuantityModalOpen, adjustQuantityModalToggler] = useToggle()
  const [adjustUnitPriceModalOpen, adjustUnitPriceModalToggler] = useToggle()

  const prepAndOpenAdjustQuantityModal = (lineItem: EditLineItem) => {
    setQuantityText(`${lineItem.quantity}`)
    setActiveLineItem(lineItem)
    adjustQuantityModalToggler.on()
  }

  const prepAndOpenAdjustUnitPriceModal = (lineItem: EditLineItem) => {
    setUnitPriceText(MoneyM.toString(lineItem.unitPrice))
    setActiveLineItem(lineItem)
    adjustUnitPriceModalToggler.on()
  }

  const removeAction = (lineItem: EditLineItem) => {
    const newAdditions = { ...additions }
    delete newAdditions[lineItem.product.id]
    setAdditions(newAdditions)

    // needed to remove "Item already in order"
    const existingLineItems = Object.keys(newAdditions)
      .filter((key) => !newAdditions[key].id)
      .map((key) => newAdditions[key])

    const idsInOrder = existingLineItems.map((li) => li.product.id)
    const productSearchResults = productsFromQuery.map((p) => ({
      ...p,
      inOrder: idsInOrder?.includes(p.id) || false,
    }))
    setProductsFromQuery(productSearchResults)
  }

  const adjustAvailabilityAction = () => {
    msgsMgr.clear()

    if (!activeLineItem) return

    if (availableAt) {
      setAdditions({
        ...additions,
        [activeLineItem.product.id]: {
          ...activeLineItem,
          availableAt,
          inStock: false,
        },
      })

      setAvailableAt(undefined)
      availabilityModalToggler.off()
    } else {
      msgsMgr.add('Availability date is required', 'negative')
    }
  }

  const adjustQuantityAction = () => {
    if (!activeLineItem) return
    const quantity = quantityText === '' ? 0 : parseInt(quantityText, 10)
    if (quantity === 0) {
      removeAction(activeLineItem)
    } else if (quantity !== activeLineItem.quantity) {
      const newAdditions = {
        ...additions,
        [activeLineItem.product.id]: {
          ...activeLineItem,
          quantity,
          extendedPrice: MoneyM.mult(activeLineItem.discountedUnitPrice, quantity),
        },
      }
      // Update the change quantity
      setAdditions(newAdditions)
    }
    adjustQuantityModalToggler.off()
  }

  const adjustUnitPriceAction = () => {
    if (!activeLineItem) return
    const unitPrice = MoneyM.fromString(
      unitPriceText === '' ? '0' : unitPriceText,
      activeLineItem.unitPrice.currency.code
    )
    if (!MoneyM.equals(unitPrice, activeLineItem.unitPrice)) {
      const activeStoreOrder = newStoreOrders.find(
        (newStoreOrder) => newStoreOrder.store.id === activeLineItem.product.store.id
      )
      const discountedUnitPrice = StoreOrderM.getDiscountedUnitPriceDecimal(
        { discounts: activeStoreOrder?.store.discount ? [activeStoreOrder.store.discount] : [] },
        MoneyM.toDecimal(unitPrice)
      )
      const extendedPrice = discountedUnitPrice * activeLineItem.quantity
      // Update the change unit price
      setAdditions({
        ...additions,
        [activeLineItem.product.id]: {
          ...activeLineItem,
          unitPrice,
          discountedUnitPrice: MoneyM.fromDecimal(discountedUnitPrice, unitPrice.currency.code),
          extendedPrice: MoneyM.fromDecimal(extendedPrice, unitPrice.currency.code),
        },
      })
    }
    adjustUnitPriceModalToggler.off()
  }

  const adjustShippingCost = () => {
    if (!shippingStoreId || !shippingText || Number.isNaN(parseFloat(shippingText))) return

    const newShippingCost = { storeId: shippingStoreId, shippingCost: shippingText }
    const shippingCost = newStoreOrders.find((so) => so.store.id === shippingStoreId)?.shippingCost

    if (shippingCost) {
      const remaining = shippingCosts.filter((s) => s.storeId !== shippingStoreId)
      setShippingCosts(remaining.concat(newShippingCost))
    } else {
      setShippingCosts(shippingCosts.concat(newShippingCost))
    }

    setShippingStoreId(undefined)
    shippingModalToggler.off()
  }

  const prepAndOpenShippingModal = (storeId) => {
    const storeShippingCost = shippingCosts.find((s) => s.storeId === storeId) || undefined

    if (storeShippingCost && storeShippingCost.shippingCost) {
      const startingAmount = MoneyM.fromString(storeShippingCost.shippingCost, 'USD')
      setShippingText(`${MoneyM.toDecimal(startingAmount)}`)
    } else {
      const defaultAmount = MoneyM.fromString('0', 'USD')
      setShippingText(`${MoneyM.toDecimal(defaultAmount)}`)
    }

    setShippingStoreId(storeId)
    shippingModalToggler.on()
  }

  const updateStoreForm = (
    storeForm: CreateQuoteStoreForm,
    updates: Partial<CreateQuoteStoreForm>
  ) => {
    const otherStoreForms = form.stores.filter((s) => s.id !== storeForm.id)
    const storeForms = [...otherStoreForms, { ...storeForm, ...updates }]
    setForm({ ...form, stores: storeForms })
  }

  const prepAndOpenAltMpnModal = (lineItem: EditLineItem) => {
    setActiveLineItem(lineItem)
    altMpnModalToggler.on()
  }

  const prepAndOpenAvailabilityModal = (lineItem: EditLineItem) => {
    if (lineItem.inStock) {
      setActiveLineItem(lineItem)
      availabilityModalToggler.on()
    } else {
      const newAdditions = {
        ...additions,
        [lineItem.product.id]: {
          ...lineItem,
          availableAt: null,
          inStock: true,
        },
      } as unknown as EditLineItemChanges

      setAdditions(newAdditions)
    }
  }

  return (
    <>
      <ModalForm
        open={availabilityModalOpen}
        onClose={availabilityModalToggler.off}
        title="Availability"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustAvailabilityAction()
        }}
      >
        <p className="mb-6">
          Adjust the availability for{' '}
          <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <Label>Select Date</Label>
        <DateInput
          value={availableAt}
          min={TimeM.toDateInputFormat(DateTime.now())}
          onChange={(value) => {
            setAvailableAt(value)
          }}
        />
      </ModalForm>
      <ModalForm
        open={adjustQuantityModalOpen}
        onClose={adjustQuantityModalToggler.off}
        title="Adjust Quantity"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustQuantityAction()
        }}
      >
        <p className="mb-2">
          Adjust the quantity for <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <Label>Quantity</Label>
        <input
          onChange={(e) => setQuantityText(e.target.value)}
          type="number"
          min={0}
          onKeyDown={() => false}
          value={quantityText}
          className="focus:ring-indigo-500 focus:border-indigo-500 block sm:text-sm border-gray-300 rounded-md w-full"
        />
      </ModalForm>
      <ModalForm
        open={adjustUnitPriceModalOpen}
        onClose={adjustUnitPriceModalToggler.off}
        title="Adjust Unit Price"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustUnitPriceAction()
        }}
      >
        <p className="mb-1">
          Adjust the unit price for{' '}
          <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <p className="mb-2">
          <span className="font-semibold">Note:</span> This only changes the unit price on this
          order. This does NOT change the price of this product anywhere else on Gearflow.
        </p>
        <Label>Unit Price</Label>
        <PriceInput
          onChange={(e) => setUnitPriceText(e.target.value)}
          min={0}
          onKeyDown={() => false}
          value={unitPriceText}
        />
      </ModalForm>
      <ModalForm
        open={shippingModalOpen}
        onClose={shippingModalToggler.off}
        title="Add Shipping"
        submitButtonText="Confirm"
        onSubmit={(e) => {
          e.preventDefault()
          adjustShippingCost()
        }}
      >
        <Label>Shipping</Label>
        <PriceInput
          onChange={(e) => setShippingText(e.target.value)}
          min={0}
          onKeyDown={() => false}
          value={shippingText}
        />
      </ModalForm>
      <ModalForm
        open={altMpnModalOpen}
        onClose={altMpnModalToggler.off}
        title="Edit Alt Mpn"
        onSubmit={(e) => {
          e.preventDefault()

          if (activeLineItem) {
            addAltMpn(activeLineItem.product.id, altMpn)
              .then((_result) => {
                if (activeLineItem) {
                  const newAdditions = {
                    ...additions,
                    [activeLineItem.product.id]: {
                      ...activeLineItem,
                      product: { ...activeLineItem.product, altMpn },
                    },
                  }
                  setAdditions(newAdditions)
                }

                msgsMgr.add('Alt MPN Updated!', 'positive')
              })
              .catch((err: GraphQLError) => {
                msgsMgr.add(err.message || UNKNOWN_ERROR, 'negative')
              })
              .finally(altMpnModalToggler.off)
          }
        }}
        submitButtonText="Update"
      >
        <TextField
          label="Alt Mpn"
          defaultValue={activeLineItem?.product.altMpn || ''}
          onChange={(e) => setAltMpn(e.target.value)}
        />
      </ModalForm>
      {newStoreOrders.map((newStoreOrder) => {
        const storeForm =
          form.stores.find((s) => s.id === newStoreOrder.store.id) ||
          CreateQuoteStoreFormM.build({ id: newStoreOrder.store.id, pickupAddress: null })

        const setPickupAddress = (pickupAddress: Address) =>
          updateStoreForm(storeForm, { pickupAddress })

        const adjustTimingDetails = (timingDetails: string) =>
          updateStoreForm(storeForm, { timingDetails })

        const adjustPickup = (pickup: boolean) => {
          updateStoreForm(storeForm, { pickup })

          if (pickup) {
            // Set the shipping to $0 (for the total cost calculations)
            setShippingCosts([
              ...shippingCosts.filter((s) => s.storeId !== storeForm.id),
              { storeId: storeForm.id, shippingCost: '0' },
            ])
          } else {
            // Clear the $0 shipping cost
            setShippingCosts(shippingCosts.filter((s) => s.storeId !== storeForm.id))
          }
        }

        return (
          <NewStoreOrderCard
            newStoreOrder={newStoreOrder}
            suggestedShippingCost={
              suggestedShippingCostByStoreId
                ? suggestedShippingCostByStoreId[newStoreOrder.store.id]
                : null
            }
            prepAndOpenAdjustQuantityModal={prepAndOpenAdjustQuantityModal}
            prepAndOpenAdjustUnitPriceModal={prepAndOpenAdjustUnitPriceModal}
            prepAndOpenShippingModal={prepAndOpenShippingModal}
            prepAndOpenAltMpnModal={prepAndOpenAltMpnModal}
            prepAndOpenAvailabilityModal={prepAndOpenAvailabilityModal}
            removeAction={removeAction}
            timingDetails={storeForm.timingDetails}
            adjustTimingDetails={adjustTimingDetails}
            pickup={storeForm.pickup}
            adjustPickup={adjustPickup}
            pickupAddress={storeForm.pickupAddress}
            setPickupAddress={setPickupAddress}
            key={newStoreOrder.store.id}
          />
        )
      })}
    </>
  )
}

export default NewStoreOrders
